/* MUI */
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';

/* Objects */
import {
    Application,
    ApplicationCategoryAndSubCategory
} from 'objects/Application';

/* React */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';

const headersFn = ({ period, renderingScheduledReport, t }) => {
    const headers = [];

    if (!renderingScheduledReport) {
        headers.push({
            align: 'left',
            field: 'ilo_uncollapse',
            renderHeader: <strong />,
            width: '5%'
        });
    }

    headers.push(
        {
            align: 'left',
            field: 'ilo_application',
            renderHeader: <strong>{t('Common.application')}</strong>,
            width: '25%'
        },
        {
            align: 'left',
            field: 'ilo_application_category',
            renderHeader: <strong>{t('Common.category')}</strong>,
            width: '25%'
        },
        {
            align: 'right',
            field: 'ilo_active',
            renderHeader: (
                <Box display="flex" flexDirection="column" width="100%">
                    <Box>
                        <span style={{ fontWeight: 600 }}>
                            {t('Common.users')}
                        </span>
                    </Box>
                    <Box>
                        <small style={{ fontWeight: 600 }}>
                            ({t('Common.all_time')})
                        </small>
                    </Box>
                </Box>
            ),
            width: '15%'
        },
        {
            align: 'right',
            field: 'ilo_add',
            renderHeader: (
                <Box display="flex" flexDirection="column" width="100%">
                    <Box>
                        <span style={{ fontWeight: 600 }}>
                            {t('Common.added')}
                        </span>
                    </Box>
                    <Box>
                        <small style={{ fontWeight: 600 }}>
                            ({t('Common.during')} {period})
                        </small>
                    </Box>
                </Box>
            ),
            width: '15%'
        },
        {
            align: 'right',
            field: 'ilo_remove',
            renderHeader: (
                <Box display="flex" flexDirection="column" width="100%">
                    <Box>
                        <span style={{ fontWeight: 600 }}>
                            {t('Common.removed')}
                        </span>
                    </Box>
                    <Box>
                        <small style={{ fontWeight: 600 }}>
                            ({t('Common.during')} {period})
                        </small>
                    </Box>
                </Box>
            ),
            width: '15%'
        }
    );
    return headers;
};

const LibraryReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup } = props;

    if (typeof reportData === 'undefined') {
        return null;
    }
    return (
        <>
            {reportData.individualLibraryOverview && (
                <LibraryOverview
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.individualLibraryOverview}
                />
            )}
            {reportData.organizationalLibraryOverview && (
                <LibraryOverview
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.organizationalLibraryOverview}
                />
            )}
        </>
    );
};

const LibraryOverviewTable = ({
    period,
    renderingScheduledReport,
    renderRow,
    stateReportData
}) => {
    const { t } = useTranslation();

    const reportData = stateReportData.map((a) => ({
        ...a,
        active: a.userLinks.length
    }));

    const rowsPerPage = renderingScheduledReport ? -1 : 25;

    const tableHeaders = useMemo(
        () =>
            headersFn({
                period,
                renderingScheduledReport,
                t
            }),
        [period, renderingScheduledReport, t]
    );

    return (
        <ManagedTable
            headers={tableHeaders}
            manageRows
            renderBody={renderRow}
            renderBodyKey="applicationId"
            renderFooter={!renderingScheduledReport}
            rows={reportData}
            rowsPerPage={rowsPerPage}
            searchEnabled={false}
            sortDirection="DESC"
            sortFieldType="number"
            sortKey="active"
        />
    );
};

class LibraryOverview extends Component {
    constructor(props) {
        super(props);

        this.state = {
            reportData: this.props.reportData
        };
    }

    setOpen = (row) => {
        const { reportData } = this.state;
        const { data } = reportData;

        data.forEach((app) => {
            if (app.application?.id === row.application.id) {
                /* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["app"] }] */
                app.open = !app.open;
            }
        });

        this.setState({ reportData: { ...reportData, data } });
    };

    renderRow = (row) => {
        const {
            renderingScheduledReport,
            reportData: propsReportData,
            t
        } = this.props;
        return (
            <React.Fragment key={`ilo_${row.application.id}`}>
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    <Application
                        data={row.application}
                        fromPage={t('Reports.Title.organizational.report')}
                        nameHasLink
                    >
                        {!renderingScheduledReport && (
                            <TableCell>
                                <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => {
                                        this.setOpen(row);
                                    }}
                                    sx={{ color: 'icon.textColor' }}
                                >
                                    {row.open ? (
                                        <KeyboardArrowUp />
                                    ) : (
                                        <KeyboardArrowDown />
                                    )}
                                </IconButton>
                            </TableCell>
                        )}
                        <TableCell>
                            <ApplicationNameLinkCell nameNoTruncate />
                        </TableCell>
                        <TableCell>
                            <ApplicationCategoryAndSubCategory
                                size="normal"
                                noTruncate
                            />
                        </TableCell>
                        <TableCell align="right">{row.active}</TableCell>
                        <TableCell align="right">{row.add}</TableCell>
                        <TableCell align="right">{row.remove}</TableCell>
                    </Application>
                </TableRow>
                <TableRow
                    variant="noHover"
                    sx={{
                        backgroundColor: 'table.subtableBackground'
                    }}
                >
                    <TableCell
                        sx={{
                            paddingBottom: 0,
                            paddingTop: 0
                        }}
                        colSpan={6}
                    >
                        <Collapse in={row.open} timeout="auto" unmountOnExit>
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: 'table.subtableBackground'
                                }}
                            >
                                <Box margin={1} padding={2}>
                                    <Typography variant="h5">
                                        History
                                    </Typography>
                                    <Table size="small" aria-label="purchases">
                                        <TableHead
                                            sx={{
                                                backgroundColor:
                                                    'table.headerRowBackground'
                                            }}
                                        >
                                            <TableRow>
                                                <TableCell width="200px">
                                                    <strong>
                                                        {t('Common.date')}
                                                    </strong>
                                                </TableCell>
                                                <TableCell width="300px">
                                                    <strong>
                                                        {t('Common.user')}
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    <strong>
                                                        {t('Common.action')}
                                                    </strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {row.data.map((data) => (
                                                <TableRow
                                                    key={`ilo_bd_${data.activityTimeStamp}`}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        {new Date(
                                                            data.activityTimeStamp
                                                        ).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        {
                                                            propsReportData
                                                                .users[
                                                                data
                                                                    .platformUserId
                                                            ].firstName
                                                        }{' '}
                                                        {
                                                            propsReportData
                                                                .users[
                                                                data
                                                                    .platformUserId
                                                            ].lastName
                                                        }
                                                    </TableCell>
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                    >
                                                        {data.activity}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Paper>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    };

    render() {
        const { renderingScheduledReport, reportSetup, t } = this.props;
        const { reportData: stateReportData } = this.state;

        let period = '';
        switch (reportSetup.period) {
            case 'daily':
                period = t('Common.day');
                break;
            case 'weekly':
                period = t('Common.week');
                break;
            case 'monthly':
                period = t('Common.month');
                break;
            default:
                period = '';
                break;
        }

        return (
            <Box width="100%" mt="32px">
                <Typography variant="h5">
                    {t('Reports.LibraryReport.library_overview')}
                </Typography>
                <LibraryOverviewTable
                    period={period}
                    renderingScheduledReport={renderingScheduledReport}
                    renderRow={this.renderRow}
                    stateReportData={stateReportData.data}
                />
            </Box>
        );
    }
}
export default withTranslation()(LibraryReports);
